import React, { useState } from 'react';
import { Clock, Play, Square, Edit3, Save, Search, Download } from 'lucide-react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { contactsApi, timeLogsApi } from '@/core/adapter-api';
import { Modal, StatusBadge, FormField, StatsGrid, EmptyState } from './shared';

interface TimeEntry {
  id: string;
  name: string;
  checkIn: string;
  checkOut: string;
  hours: number;
  status: string;
}

export const ControlHorarioPage: React.FC = () => {
  const rawLogs = useQuery(async () => {
    const { getAdapter } = await import('@/core/db-adapter');
    return getAdapter().query<any>('time_logs');
  }, [], ['time_logs']) || [];

  const rawContacts = useQuery(() => contactsApi.all(), [], ['contacts']) || [];

  const entries: (TimeEntry & { logId: string; contactId: string })[] = rawContacts
    .filter((c: any) => c.isWorker)
    .map((worker: any) => {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      
      const log = rawLogs.find((l: any) => l.contactId === worker.id && l.startTime >= todayStart.getTime());

      if (log) {
        const inD = new Date(log.startTime);
        const checkIn = `${inD.getHours().toString().padStart(2, '0')}:${inD.getMinutes().toString().padStart(2, '0')}`;
        let checkOut = '';
        let hours = 0;
        if (log.endTime) {
          const outD = new Date(log.endTime);
          checkOut = `${outD.getHours().toString().padStart(2, '0')}:${outD.getMinutes().toString().padStart(2, '0')}`;
          hours = Math.round((log.endTime - log.startTime) / (1000 * 60 * 60) * 10) / 10;
        } else {
          hours = Math.round((Date.now() - log.startTime) / (1000 * 60 * 60) * 10) / 10;
        }

        return {
          id: worker.id,
          logId: log.id,
          contactId: worker.id,
          name: worker.name,
          checkIn,
          checkOut,
          hours,
          status: log.status === 'completed' ? 'Completado' : 'Activo'
        };
      } else {
        return {
          id: worker.id,
          logId: '',
          contactId: worker.id,
          name: worker.name,
          checkIn: '',
          checkOut: '',
          hours: 0,
          status: 'Pendiente'
        };
      }
    });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState<(TimeEntry & { logId: string; contactId: string }) | null>(null);

  const totalHours = entries.reduce((s, e) => s + e.hours, 0);
  const avgHours = entries.filter(e => e.hours > 0).length > 0
    ? (totalHours / entries.filter(e => e.hours > 0).length).toFixed(1)
    : '0';
  const extraHours = entries.reduce((s, e) => s + Math.max(0, e.hours - 8), 0);
  const absences = entries.filter(e => e.status === 'Pendiente').length;
  const [searchQuery, setSearchQuery] = useState('');
  const filteredEntries = searchQuery
    ? entries.filter(e => e.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : entries;

  const clockIn = async (contactId: string) => {
    await timeLogsApi.add({
      contactId,
      obraId: 'general',
      startTime: Date.now(),
      status: 'active'
    } as any);
  };

  const clockOut = async (logId: string) => {
    if (!logId) return;
    await timeLogsApi.update(logId, {
      endTime: Date.now(),
      status: 'completed'
    } as any);
  };

  const openEdit = (entry: TimeEntry & { logId: string; contactId: string }) => {
    setEditData({ ...entry });
    setEditModal(true);
  };

  const saveEdit = async () => {
    if (editData && editData.logId) {
      const now = new Date();
      const [inH, inM] = editData.checkIn.split(':').map(Number);
      const startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), inH, inM).getTime();
      let endTime: number | undefined = undefined;

      if (editData.checkOut) {
        const [outH, outM] = editData.checkOut.split(':').map(Number);
        endTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), outH, outM).getTime();
      }

      await timeLogsApi.update(editData.logId, {
        startTime,
        endTime,
        status: endTime ? 'completed' : 'active'
      } as any);
      
      setEditModal(false);
      setEditData(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/15 rounded-xl">
            <Clock size={20} className="text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Control Horario</h1>
            <p className="text-sm text-gray-500">Registro de jornadas y fichajes</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar trabajador..."
              className="pl-9 pr-3 py-2 bg-[#0d1117] border border-white/10 rounded-xl text-sm text-gray-200 placeholder:text-gray-600 focus:border-amber-500/50 focus:outline-none w-48"
            />
          </div>
          <button
            onClick={() => {
              const headers = ['Nombre', 'Entrada', 'Salida', 'Horas', 'Estado'];
              const rows = entries.map(e => [e.name, e.checkIn || '-', e.checkOut || '-', String(e.hours), e.status]);
              const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `fichajes_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-[#0d1117] border border-white/10 rounded-xl text-sm text-gray-300 hover:border-amber-500/50 transition-colors"
            title="Exportar CSV"
          >
            <Download size={14} /> CSV
          </button>
        </div>
      </div>

      <StatsGrid stats={[
        { label: 'Horas esta semana', value: `${totalHours}h` },
        { label: 'Media diaria', value: `${avgHours}h` },
        { label: 'Horas extra', value: `${extraHours}h`, color: 'text-amber-400' },
        { label: 'Ausencias', value: String(absences), color: 'text-red-400' },
      ]} />

      {/* Weekly Activity Heat Strip */}
      <div className="bg-[#0d1117] border border-white/8 rounded-xl p-4 mb-0">
        <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3">Actividad semanal</h3>
        <div className="flex gap-1.5">
          {Array.from({ length: 7 }).map((_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (6 - i));
            d.setHours(0, 0, 0, 0);
            const nextD = new Date(d);
            nextD.setDate(nextD.getDate() + 1);
            const dayLogs = rawLogs.filter((l: any) => l.startTime >= d.getTime() && l.startTime < nextD.getTime());
            let dayHours = 0;
            dayLogs.forEach((l: any) => {
              const end = l.endTime || Date.now();
              dayHours += (end - l.startTime) / (1000 * 60 * 60);
            });
            const isToday = i === 6;
            const intensity = Math.min(dayHours / 10, 1); // 10h = max intensity
            const dayName = d.toLocaleDateString('es-ES', { weekday: 'short' });
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className={`w-full h-8 rounded-md transition-all ${isToday ? 'ring-1 ring-amber-500/30' : ''}`}
                  style={{ backgroundColor: dayHours > 0 ? `rgba(245, 158, 11, ${0.15 + intensity * 0.6})` : 'rgba(255,255,255,0.03)' }}
                  title={`${dayName}: ${dayHours.toFixed(1)}h`}
                />
                <span className={`text-[9px] uppercase font-bold ${isToday ? 'text-amber-400' : 'text-gray-600'}`}>{dayName}</span>
                {dayHours > 0 && <span className="text-[9px] text-gray-500 font-bold">{dayHours.toFixed(0)}h</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Fichajes de hoy</h3>
          {entries.filter(e => e.status === 'Activo').length > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] text-emerald-400 font-bold">{entries.filter(e => e.status === 'Activo').length} en obra</span>
            </div>
          )}
        </div>
        <div className="divide-y divide-white/5">
          {filteredEntries.length === 0 && (
            <EmptyState
              icon={Clock}
              title="Sin fichajes hoy"
              description="Los fichajes aparecerán aquí cuando los trabajadores registren su jornada."
            />
          )}
          {filteredEntries.map((entry) => (
            <div
              key={entry.id}
              className={`flex items-center justify-between px-5 py-3 cursor-pointer transition-colors ${
                selectedId === entry.id ? 'bg-amber-500/5' : 'hover:bg-white/3'
              }`}
              onClick={() => setSelectedId(selectedId === entry.id ? null : entry.id)}
            >
              <div>
                <p className="text-sm text-gray-200 font-medium">{entry.name}</p>
                <p className="text-xs text-gray-500">
                  {entry.checkIn
                    ? `Entrada: ${entry.checkIn}${entry.checkOut ? ` - Salida: ${entry.checkOut}` : ' - En curso'} · ${entry.hours}h`
                    : 'No fichado'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {entry.status === 'Pendiente' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); clockIn(entry.contactId); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 transition-colors"
                    title="Fichar entrada"
                  >
                    <Play size={12} /> Entrada
                  </button>
                )}
                {entry.status === 'Activo' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); clockOut(entry.logId); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition-colors"
                    title="Fichar salida"
                  >
                    <Square size={12} /> Salida
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); openEdit(entry); }}
                  className="p-1.5 text-gray-500 hover:text-amber-400 transition-colors"
                  title="Editar horas"
                >
                  <Edit3 size={14} />
                </button>
                <StatusBadge status={entry.status} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Modal open={editModal} onClose={() => setEditModal(false)} title="Editar fichaje">
        {editData && (
          <div className="space-y-4">
            <p className="text-sm text-gray-300 font-semibold">{editData.name}</p>
            <FormField label="Hora entrada" value={editData.checkIn} onChange={(v) => setEditData({ ...editData, checkIn: v })} type="time" />
            <FormField label="Hora salida" value={editData.checkOut} onChange={(v) => setEditData({ ...editData, checkOut: v })} type="time" />
            <FormField label="Horas totales" value={String(editData.hours)} onChange={(v) => setEditData({ ...editData, hours: parseFloat(v) || 0 })} type="number" />
            <button
              onClick={saveEdit}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 transition-colors"
            >
              <Save size={16} /> Guardar
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
};
